import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-warning/10 text-warning',
    approved: 'bg-success/10 text-success',
    rejected: 'bg-error/10 text-error',
    processing: 'bg-primary/10 text-primary',
    flagged: 'bg-orange-500/10 text-orange-400',
  };
  return (
    <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${map?.[status] || 'bg-muted text-muted-foreground'}`}>
      {status}
    </span>
  );
};

const SubmissionDetailModal = ({ submission, onClose, onApprove, onReject, onFlag }) => {
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  return (
    <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4">
      <div className="bg-card rounded-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h3 className="text-base font-semibold text-foreground">Submission Detail</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="X" size={16} />
          </button>
        </div>
        <div className="p-5 space-y-5">
          {/* Influencer info */}
          <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="User" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">{submission?.userName}</p>
              <p className="text-xs text-muted-foreground">{submission?.userEmail}</p>
            </div>
            <div className="ml-auto text-right">
              <p className="text-xs text-muted-foreground">SC Score</p>
              <p className="text-sm font-bold text-primary">{submission?.userScore}</p>
            </div>
          </div>

          {/* Transaction details */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Brand / Campaign', value: submission?.brandName },
              { label: 'Purchase Amount', value: `$${parseFloat(submission?.purchaseAmount || 0)?.toFixed(2)}` },
              { label: 'Calculated Cashback', value: `$${parseFloat(submission?.rewardAmount || 0)?.toFixed(2)}` },
              { label: 'Submitted', value: submission?.submittedAt },
            ]?.map((d, i) => (
              <div key={i} className="bg-muted/40 rounded-lg p-3">
                <p className="text-xs text-muted-foreground mb-1">{d?.label}</p>
                <p className="text-sm font-semibold text-foreground">{d?.value}</p>
              </div>
            ))}
          </div>

          {/* Receipt */}
          {submission?.receiptUrl && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Receipt</p>
              <a href={submission?.receiptUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-muted/40 rounded-lg hover:bg-muted transition-colors">
                <Icon name="FileImage" size={16} color="var(--color-primary)" />
                <span className="text-sm text-primary">View Receipt</span>
                <Icon name="ExternalLink" size={12} className="ml-auto text-muted-foreground" />
              </a>
            </div>
          )}

          {/* Social post */}
          {submission?.socialPostUrl && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Social Post</p>
              <a href={submission?.socialPostUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 p-3 bg-muted/40 rounded-lg hover:bg-muted transition-colors">
                <Icon name="Link" size={16} color="var(--color-secondary)" />
                <span className="text-sm text-secondary truncate">{submission?.socialPostUrl}</span>
                <Icon name="ExternalLink" size={12} className="ml-auto text-muted-foreground flex-shrink-0" />
              </a>
            </div>
          )}

          {/* Rejection reason input */}
          {rejecting && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Rejection Reason</p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e?.target?.value)}
                placeholder="Provide a reason for rejection..."
                className="w-full bg-muted border border-border rounded-lg p-3 text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                rows={3}
              />
            </div>
          )}

          {/* Actions */}
          {submission?.status === 'pending' && (
            <div className="flex gap-2">
              {!rejecting ? (
                <>
                  <Button variant="default" size="sm" iconName="CheckCircle" onClick={() => { onApprove(submission?.id); onClose(); }} className="flex-1">
                    Approve
                  </Button>
                  <Button variant="outline" size="sm" iconName="XCircle" onClick={() => setRejecting(true)} className="flex-1">
                    Reject
                  </Button>
                  <Button variant="ghost" size="sm" iconName="Flag" onClick={() => { onFlag(submission?.id); onClose(); }}>
                    Flag
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={() => setRejecting(false)} className="flex-1">Cancel</Button>
                  <Button variant="destructive" size="sm" onClick={() => { onReject(submission?.id, rejectReason); onClose(); }} className="flex-1">Confirm Reject</Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ModerationQueue = ({ currentAdminId }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('transactions')
        ?.select(`id, amount, purchase_value, status, created_at, social_post_url, receipt_url, rejection_reason,
          campaigns(title),
          influencer:user_profiles!transactions_influencer_id_fkey(id, full_name, email),
          scores!inner(total_score)`)
        ?.order('created_at', { ascending: false })
        ?.limit(100);
      if (!error && data) {
        const mapped = data?.map(t => ({
          id: t?.id,
          userName: t?.influencer?.full_name || t?.influencer?.email || 'User',
          userEmail: t?.influencer?.email || '',
          userScore: t?.scores?.total_score || 0,
          influencerId: t?.influencer?.id,
          brandName: t?.campaigns?.title || 'Campaign',
          purchaseAmount: parseFloat(t?.purchase_value) || 0,
          rewardAmount: parseFloat(t?.amount) || 0,
          receiptUrl: t?.receipt_url || '',
          socialPostUrl: t?.social_post_url || '',
          submittedAt: new Date(t?.created_at)?.toLocaleDateString(),
          status: t?.status || 'pending',
          rejectionReason: t?.rejection_reason || '',
        }));
        setSubmissions(mapped);
      }
    } catch (err) {
      console.error('[ModerationQueue] loadSubmissions error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  const handleApprove = async (id) => {
    try {
      const { error } = await supabase?.from('transactions')?.update({ status: 'approved', reviewed_by: currentAdminId, reviewed_at: new Date()?.toISOString() })?.eq('id', id);
      if (!error) {
        setSubmissions(prev => prev?.map(s => s?.id === id ? { ...s, status: 'approved' } : s));
        await supabase?.from('moderation_logs')?.insert({ moderator_id: currentAdminId, transaction_id: id, action: 'approved' });
      }
    } catch (err) { console.error('[ModerationQueue] handleApprove error:', err); }
  };

  const handleReject = async (id, reason) => {
    try {
      const { error } = await supabase?.from('transactions')?.update({ status: 'rejected', reviewed_by: currentAdminId, reviewed_at: new Date()?.toISOString(), rejection_reason: reason || '' })?.eq('id', id);
      if (!error) {
        setSubmissions(prev => prev?.map(s => s?.id === id ? { ...s, status: 'rejected' } : s));
        await supabase?.from('moderation_logs')?.insert({ moderator_id: currentAdminId, transaction_id: id, action: 'rejected', reason: reason || '' });
      }
    } catch (err) { console.error('[ModerationQueue] handleReject error:', err); }
  };

  const handleFlag = async (id) => {
    try {
      const { error } = await supabase?.from('transactions')?.update({ status: 'processing' })?.eq('id', id);
      if (!error) {
        setSubmissions(prev => prev?.map(s => s?.id === id ? { ...s, status: 'processing' } : s));
        await supabase?.from('moderation_logs')?.insert({ moderator_id: currentAdminId, transaction_id: id, action: 'flagged', notes: 'Flagged for review' });
      }
    } catch (err) { console.error('[ModerationQueue] handleFlag error:', err); }
  };

  const filtered = submissions?.filter(s => {
    const matchSearch = s?.userName?.toLowerCase()?.includes(search?.toLowerCase()) || s?.brandName?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = statusFilter === 'all' || s?.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const pendingCount = submissions?.filter(s => s?.status === 'pending')?.length;

  return (
    <div className="space-y-5">
      {pendingCount > 0 && (
        <div className="flex items-center gap-3 p-4 bg-warning/10 border border-warning/30 rounded-xl">
          <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
          <p className="text-sm font-medium text-warning">{pendingCount} submission{pendingCount !== 1 ? 's' : ''} awaiting review</p>
        </div>
      )}
      <div className="bg-card rounded-xl border border-border p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div>
            <h3 className="text-base font-semibold text-foreground">Verification Queue</h3>
            <p className="text-sm text-muted-foreground">{filtered?.length} submissions</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="w-48"><Input type="search" placeholder="Search..." value={search} onChange={(e) => setSearch(e?.target?.value)} /></div>
            <div className="w-36">
              <Select
                options={[{ value: 'all', label: 'All Status' }, { value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }, { value: 'processing', label: 'Flagged' }]}
                value={statusFilter} onChange={setStatusFilter}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  {['User', 'Campaign', 'Purchase', 'Cashback', 'Post URL', 'Submitted', 'Status', 'Actions']?.map(h => (
                    <th key={h} className="text-left py-3 px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered?.map((s) => (
                  <tr key={s?.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-3">
                      <p className="text-sm font-medium text-foreground">{s?.userName}</p>
                      <p className="text-xs text-muted-foreground">Score: {s?.userScore}</p>
                    </td>
                    <td className="py-3 px-3"><span className="text-sm text-foreground max-w-[120px] truncate block">{s?.brandName}</span></td>
                    <td className="py-3 px-3"><span className="text-sm font-medium text-foreground">${s?.purchaseAmount?.toFixed(2)}</span></td>
                    <td className="py-3 px-3"><span className="text-sm font-semibold text-success">${s?.rewardAmount?.toFixed(2)}</span></td>
                    <td className="py-3 px-3">
                      {s?.socialPostUrl ? (
                        <a href={s?.socialPostUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1">
                          <Icon name="ExternalLink" size={11} /> View Post
                        </a>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="py-3 px-3"><span className="text-xs text-muted-foreground">{s?.submittedAt}</span></td>
                    <td className="py-3 px-3"><StatusBadge status={s?.status} /></td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelectedSubmission(s)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View detail">
                          <Icon name="Eye" size={14} />
                        </button>
                        {s?.status === 'pending' && (
                          <>
                            <button onClick={() => handleApprove(s?.id)} className="p-1.5 rounded-lg hover:bg-success/10 text-muted-foreground hover:text-success transition-colors" title="Approve">
                              <Icon name="Check" size={14} />
                            </button>
                            <button onClick={() => handleReject(s?.id, '')} className="p-1.5 rounded-lg hover:bg-error/10 text-muted-foreground hover:text-error transition-colors" title="Reject">
                              <Icon name="X" size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered?.length === 0 && (
                  <tr><td colSpan={8} className="py-10 text-center text-sm text-muted-foreground">No submissions found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {selectedSubmission && (
        <SubmissionDetailModal
          submission={selectedSubmission}
          onClose={() => setSelectedSubmission(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onFlag={handleFlag}
        />
      )}
    </div>
  );
};

export default ModerationQueue;
